"use client";

import React, {useState} from "react";
import {useTranslations} from "next-intl";
import {ColletteDivider, ColletteIconButton} from "@collette-travel/collette-common-controls";
import {Box, MenuItem, Select, SelectChangeEvent, Stack, Typography} from "@mui/material";
import {Close} from "@mui/icons-material";
import {ActivityLevel} from "@/types/dto/pdp/pdp-header-about";
import ActivityLevelMeterIcon from "@/components/common/activity-level-indicator/activity-level-meter-icon";

interface ActivityLevelModalProps {
  activityLevels: ActivityLevel[];
  handleClose: () => void;
  thisTourNumber: number;
  openTo: number;
}

const ActivityLevelModal = ({
  activityLevels,
  thisTourNumber,
  handleClose,
  openTo
}: ActivityLevelModalProps) => {
  const t = useTranslations("PDP");

const [currentActivityLevel, setCurrentActivityLevel] = useState<string>(openTo.toString());

const {
  description: currentDescription,
  number: currentNumber,
  shortDescription: currentTitle
} =
  activityLevels.find(level => level.number.toString() === currentActivityLevel) ?? {};

  const handleChange = (event: SelectChangeEvent) => {
    setCurrentActivityLevel(event.target.value);
  };

  const makeLabel = (levelNumber: number, levelTitle: string) => {
    if (thisTourNumber === levelNumber) {
      return `${t("Level")} ${levelNumber} - ${levelTitle} (${t("This Tour")})`;
    }
    return `${t("Level")} ${levelNumber} - ${levelTitle}`;
  };

  return (
    <Stack
      sx={{
        borderRadius: "1rem",
        backgroundColor: "background.default",
        overflow: "hidden",
        padding: {xs: "2.4rem", sm: "4rem", md: "4rem 6rem"},
        width: {xs: "auto", md: "70rem"},
        minHeight: "70rem"
      }}
      data-testid="activity-level-modal-container"
    >
      <ColletteIconButton
        testId="activity-level-modal-close-button"
        onClick={handleClose}
        sx={{
          mt: "-0.8rem",
          mr: "-0.8rem",
          position: "absolute",
          top: 24,
          right: 24
        }}
      >
        <Close />
      </ColletteIconButton>
      <Stack gap="2.4rem">
        {/* Select */}
        <Stack
          gap="1.5rem"
          alignItems="center"
          sx={{
            flexDirection: {xs: "column", sm: "row"},
            justifyContent: {xs: "start", sm: "center"},
            alignItems: {xs: "start", sm: "center"},
            marginTop: {xs: "2.4rem", md: 0}
          }}
        >
          <Typography
            sx={{
              textWrap: "nowrap",
              fontSize: "1.4rem"
            }}
            data-testid="activity-level-modal-dropdown-label"
          >
            {`${t("Activity Level")}:`}
          </Typography>
          <Select
            value={currentActivityLevel}
            onChange={handleChange}
            variant="outlined"
            sx={{
              height: "3.2rem",
              width: "100%",
              maxWidth: {md: "30rem"},
              borderRadius: "1rem"
            }}
            data-testid="activity-level-modal-dropdown"
          >
            {activityLevels.map((level) => (
              <MenuItem
                value={level.number.toString()}
                data-testid={`activity-level-modal-dropdown_item-${level.number}`}
              >
                {makeLabel(level.number, level.shortDescription)}
              </MenuItem>
            ))}
          </Select>
        </Stack>

        <ColletteDivider sx={{ borderColor: "divider", marginBottom: "-1.2rem" }} />

        <Stack direction="row" gap="1rem" alignItems="center">
          <Box padding="1.4rem 1.2rem 1.2rem">
            <ActivityLevelMeterIcon
              activityLevel={parseInt(currentActivityLevel, 10)}
              color="var(--theme-palette-secondary-600)"
              borderColor="var(--theme-palette-secondary-main)"
              emptyColor="var(--theme-palette-grey-200)"
              emptyBorderColor="var(--theme-palette-grey-400)"
              size="large"
            />
          </Box>
          <Typography
            component="p"
            variant="h5"
            sx={{ textWrap: "wrap" }}
            data-testid="activity-level-modal-header"
          >
            {t("Activity Level")} {currentNumber} - {currentTitle}
          </Typography>
        </Stack>

        {/* Description */}
        <div
          style={{
            fontSize: "1.6rem"
          }}
          data-testid={`activity-level-modal_${currentActivityLevel}_description`}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{__html: currentDescription ?? ""}}
        />
      </Stack>
    </Stack>
  );
};

export default ActivityLevelModal;